import React, { useEffect } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Label } from './ui/label';

delete L.Icon.Default.prototype._getIconUrl;

const customerIcon = new L.DivIcon({
  className: 'customer-form-marker',
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#3b82f6;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

const Recenter = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], map.getZoom() < 13 ? 15 : map.getZoom());
    }
  }, [lat, lng, map]);
  return null;
};

const CustomerLocationMap = ({ formData, setFormData }) => {
  const lat = parseFloat(formData.latitude);
  const lng = parseFloat(formData.longitude);
  const hasValidCoords = !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  const center = hasValidCoords ? [lat, lng] : [-6.2088, 106.8456];

  const handlePick = (newLat, newLng) => {
    setFormData(prev => ({
      ...prev,
      latitude: newLat.toFixed(8),
      longitude: newLng.toFixed(8),
    }));
  };

  const handleUseCurrent = () => {
    if (!navigator.geolocation) {
      alert('Browser tidak mendukung geolocation');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => handlePick(pos.coords.latitude, pos.coords.longitude),
      (err) => alert('Gagal mendapatkan lokasi: ' + err.message)
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-medium flex items-center gap-1.5">
          <MapPin className="w-4 h-4 text-blue-600" />
          Preview Lokasi di Peta
        </Label>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="customer-form-use-current-location"
            onClick={handleUseCurrent}
            className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
          >
            <MapPin className="w-3 h-3" /> Lokasi Saya
          </button>
          {hasValidCoords && (
            <a
              data-testid="customer-form-open-google-maps"
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Buka di Google Maps
            </a>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        {hasValidCoords
          ? 'Klik di peta untuk mengubah lokasi.'
          : 'Klik pada peta untuk memilih lokasi pelanggan, atau gunakan tombol "Lokasi Saya".'}
      </p>
      <div className="rounded-md overflow-hidden border" style={{ height: '280px' }}>
        <MapContainer center={center} zoom={hasValidCoords ? 15 : 11} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={handlePick} />
          {hasValidCoords && (
            <>
              <Recenter lat={lat} lng={lng} />
              <Marker position={[lat, lng]} icon={customerIcon} />
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default CustomerLocationMap;
